import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@src/design-system/button';

import { createRoute, PATHS, PUBLIC_PATH } from '@src/core/router/paths';
import Loader from '@shared/components/reusables/loader';
import { ArrowLeft, Home } from 'lucide-react';
import { useGetMemberships } from '@src/shared/services/get-memberships';
import { getCredentials } from '@src/shared/utils/get-credentials';

export default function NotFound() {
  const { refreshToken } = getCredentials();
  const { organizationSlug } = useParams<{ organizationSlug: string }>();

  const { data: memberships, isPending } = useGetMemberships({
    select: (data) => data.filter((m) => m.is_active),
  });
  const navigate = useNavigate();

  const isCurrentOrgSlugValid = memberships?.some(
    (membership) => membership.organization.slug === organizationSlug
  );

  const orgSlug = isCurrentOrgSlugValid
    ? organizationSlug
    : memberships?.[0]?.organization.slug;
  const goHome = () => {
    if (orgSlug) {
      navigate(createRoute(PATHS.PROGRAMS, orgSlug));
    } else {
      navigate(PUBLIC_PATH.LOGIN);
    }
  };

  const goBack = () => navigate(-1);

  if (isPending && refreshToken) return <Loader />;

  return (
    <div className='min-h-screen bg-primary-50 flex items-center justify-center px-4 py-10'>
      <div className='w-full max-w-2xl bg-white border border-pale-border rounded-xl shadow-sm p-8 text-center'>
        <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-50 text-gray-600'>
          <span className='text-2xl font-semibold'>404</span>
        </div>

        <h1 className='text-2xl font-semibold text-gray-900'>Page not found</h1>
        <p className='mt-2 text-gray-700'>
          The page you are looking for does not exist or might have been moved.
        </p>

        <div className='mt-6 flex flex-col sm:flex-row gap-3 justify-center'>
          <Button onClick={goHome}>
            <Home className='w-4 h-4' />
            <span>{orgSlug ? 'Go to home' : 'Login'}</span>
          </Button>
          <Button
            onClick={goBack}
            variant='primaryOutline'
          >
            <ArrowLeft className='w-4 h-4' />
            <span>Go Back</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
